import {AGENCY_KEYS} from "@/api/agencyApi";
import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import {INFLUENCER_KEYS} from "@/api/influencerApi";
import type {
  NotificationPreferenceListSerializer,
  UserBankAccountSerializer,
  UserDetailSerializer,
  UserSerializer,
} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedAgency, useSelectedCompany, useUserId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import useCampaignFilterStore from "@/store/campaignFilter";
import {languagePreferenceAtom} from "@/store/jotaiAtoms";
import type {TLanguage, TPaginated} from "@/store/types";
import {useMutation, type InfiniteData} from "@tanstack/react-query";
import {useParams} from "@tanstack/react-router";
import type {AxiosResponse} from "axios";
import {produce, type WritableDraft} from "immer";
import {useSetAtom} from "jotai";

type TVerificationUser = UserSerializer & {
  bank_account?: UserBankAccountSerializer;
  tiktok_verified: boolean;
};

export const USER_KEYS = {
  mUser: (userId: number) => [`/users/${userId}/`],
  mUserAccountVerification: (search?: string) => [`/users/verify-account/${search}`],
  mUserEmailChangeVerification: ["/users/verify-email-change/"],
  mUserAgreedPolicies: (userId: number) => [`/users/${userId}/agreed-policies/`],
  mUserEmail: (email: string) => [`/users/search_email/?email=${encodeURIComponent(email)}`],
  mUserInstagramUsernameValidation: ["/users/validate-username/"],
  mUserList: ["/users/"],
  mUserNewVerificationEmail: (userId: number) => [`/users/${userId}/new_verification_email/`],
  mUserNotificationListSeenStatus: (userId: number) => [`/users/${userId}/notifications/seen/`],
  mUserNotificationPreferences: (userId: number) => [`/users/${userId}/update_preference/`],
  mUserPasswordReset: (search?: string) => [`/users/reset-password/${search}`],
  mUserPasswordResetRequest: ["/users/reset-password-request/"],
  mUserRejectInstagram: (userId: number) => [`/users/${userId}/reject/`],
  mUserRejectTiktok: (userId: number) => [`/users/${userId}/reject_tiktok/`],
  mUserResendVerificationEmail: [`/users/resend-verification-email/`],
  mUserReset: ["/users/reset/"],
  mUserTikTokUsernameValidation: ["/users/validate-tiktok-username/"],
  mUserVerifyInstagram: (userId: number) => [`/users/${userId}/verify/`],
  mUserVerifyTiktok: (userId: number) => [`/users/${userId}/verify_tiktok/`],
  mUserVerifyUgc: (userId: number) => [`/users/${userId}/verify_ugc/`],
  qUser: (userId: number) => [`/users/${userId}/`],
  qUserMobileAppRedirect: ["/users/mobile_app/"],
  qUserNotificationCounts: (userId: number) => [`/users/${userId}/notifications_count/`],
  qUserNotificationList: (userId: number) => [`/users/${userId}/notifications/`],
  qUserNotificationPreferences: (userId: number) => [`/users/${userId}/preferences/`],
  qUserUnagreedPolicies: (userId: number) => [`/users/${userId}/unagreed-policies/`],
} as const;

export const useMobileAppRedirect = () => {
  const {data, refetch} = useGenericQuery<{redirect?: string}>({
    queryKey: USER_KEYS.qUserMobileAppRedirect,
    enabled: false,
  });

  const openAppRedirect = async () => {
    if (data?.redirect) {
      window.open(data.redirect, "_blank");
      return;
    }

    const response = await refetch();
    const url = response.data?.redirect;

    if (url) {
      location.href = url;
    }
  };

  return {openAppRedirect};
};

export const useRejectInstagramMutation = (userId: number) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserRejectInstagram(userId),
    mutationFn: ({reason}: {reason: string}) =>
      client.post<TVerificationUser>(USER_KEYS.mUserRejectInstagram(userId)[0], {reason}),
  });

export const useRejectTiktokMutation = (userId: number) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserRejectTiktok(userId),
    mutationFn: ({reason}: {reason: string}) =>
      client.post<TVerificationUser>(USER_KEYS.mUserRejectTiktok(userId)[0], {reason}),
  });

export const useUpdateUserLanguageMutation = () => {
  const {agencyId, campaignId, companyId, influencerId} = useParams({strict: false});
  const userId = useUserId();
  const selectedAgency = useSelectedAgency();
  const selectedCompany = useSelectedCompany();
  const setLanguagePreference = useSetAtom(languagePreferenceAtom);

  return useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUser(userId),
    mutationFn: ({lang}: {lang: TLanguage}) => client.patch(USER_KEYS.mUser(userId)[0], {language_code: lang}),
    onMutate: response => {
      queryClient.setQueryData<WritableDraft<AxiosResponse<UserDetailSerializer>>>(
        USER_KEYS.qUser(userId),
        prevState =>
          prevState &&
          produce(prevState, draft => {
            draft.data.language_code = response.lang;
          }),
      );
      setLanguagePreference(response.lang);
    },
    onSuccess: () => {
      const DASHBOARD_CAMPAIGN_STATUSES = ["active", "drafts", "complete", "open", "paused"] as const;

      [
        CAMPAIGN_KEYS.qCampaign(Number(campaignId)),
        CAMPAIGN_KEYS.qCampaignList(useCampaignFilterStore.getState().searchParams),
        COMPANY_KEYS.qCompanyChatCampaigns(Number(selectedCompany?.id ?? companyId)),
        ...DASHBOARD_CAMPAIGN_STATUSES.map(status =>
          AGENCY_KEYS.qAgencyCampaigns({agencyId: Number(selectedAgency?.id ?? agencyId), status}),
        ),
        ...DASHBOARD_CAMPAIGN_STATUSES.map(status =>
          COMPANY_KEYS.qCompanyCampaigns({companyId: Number(selectedCompany?.id ?? companyId), status}),
        ),
        INFLUENCER_KEYS.qInfluencerActiveCampaigns(Number(influencerId)),
        INFLUENCER_KEYS.qInfluencerCompletedContent(Number(influencerId)),
        INFLUENCER_KEYS.qInfluencerCompletedUgcApplications(Number(influencerId)),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    },
  });
};

export const useUpdateUserProfileMutation = ({
  onSuccessCallback,
  userId,
}: {
  onSuccessCallback?(): void;
  userId: number;
}) =>
  useMutation({
    mutationKey: USER_KEYS.mUser(userId),
    mutationFn: ({
      email,
      firstName,
      lastName,
      profilePicture,
    }: {
      email?: string;
      firstName: string;
      lastName: string;
      profilePicture?: File | null;
    }) => {
      const formData = new FormData();

      formData.append("first_name", firstName);
      formData.append("last_name", lastName);

      if (email) {
        formData.append("email", email);
      }

      if (profilePicture) {
        formData.append("profile_picture", profilePicture);
        formData.append("profile_picture320", profilePicture);
      }

      return client.patch(USER_KEYS.mUser(userId)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    onSuccess: onSuccessCallback,
  });

export const useUpdateUserNotificationPreferencesMutation = ({
  companyId,
  isInitiallyEnabled,
  userId,
}: {
  companyId: number;
  isInitiallyEnabled: boolean;
  userId: number;
}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserNotificationPreferences(userId),
    mutationFn: () =>
      client.post(USER_KEYS.mUserNotificationPreferences(userId)[0], {
        company_profile: companyId,
        email_enabled: !isInitiallyEnabled,
      }),
    onSuccess: () => {
      queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<NotificationPreferenceListSerializer>>>>(
        USER_KEYS.qUserNotificationPreferences(userId),
        previous =>
          previous && {
            ...previous,
            pages: previous.pages.map(page => ({
              ...page,
              data: {
                ...page.data,
                results: page.data.results.map(preference =>
                  preference.company_profile.id === companyId
                    ? {...preference, email_enabled: !isInitiallyEnabled}
                    : preference,
                ),
              },
            })),
          },
      );
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(companyId)});
    },
  });

export const useVerifyInstagramMutation = (userId: number) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserVerifyInstagram(userId),
    mutationFn: ({verify}: {verify: boolean}) =>
      client.post<TVerificationUser>(USER_KEYS.mUserVerifyInstagram(userId)[0], {verify}),
  });

export const useVerifyTiktokMutation = (userId: number) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserVerifyTiktok(userId),
    mutationFn: () => client.post<TVerificationUser>(USER_KEYS.mUserVerifyTiktok(userId)[0]),
  });

export const useVerifyUgcMutation = (userId: number) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserVerifyUgc(userId),
    mutationFn: () => client.post(USER_KEYS.mUserVerifyUgc(userId)[0]),
  });
